import { Component, Input } from '@angular/core';
import { LanguageService } from '../../../services/i18n/raksme.languageService';

/**
 * Shown when a request succeeded but returned nothing.
 *
 * Distinct from <raksme-error> on purpose: "there is nothing here" and "we
 * could not load this" are different facts, and the site previously showed the
 * same thing for both - an empty region with no explanation. 13 of 109
 * components expressed an empty state at all, each with its own ad-hoc markup.
 *
 * Announced politely rather than assertively: an empty result is information,
 * not an error, so it should not interrupt a screen reader mid-sentence.
 */
@Component({
  selector: 'raksme-empty',
  templateUrl: './empty-state.component.html',
  styleUrls: ['./empty-state.component.css']
})
export class EmptyStateComponent {

  constructor(private language: LanguageService) { }

  /** Falls back to a generic message so a caller can never render it blank. */
  @Input() message: string;

  /**
   * Language for the built-in strings.
   *
   * Defaults to the language of the current route rather than to English, so a
   * caller on an Arabic page gets Arabic without having to say so. Every
   * `lang="ar"` in the templates existed only because this defaulted to 'en';
   * they are redundant now, and as the language fork retires (D3) a single
   * component serving both languages cannot pass a hardcoded value anyway.
   *
   * An explicit binding still wins, for the rare caller that needs to override.
   */
  @Input() set lang(v: string) { this._lang = v; }
  get lang(): string { return this._lang || this.language.lang; }
  private _lang: string = null;

  get text(): string {
    if (this.message && this.message.trim()) { return this.message; }
    return this.lang === 'ar' ? 'لا توجد عناصر لعرضها.' : 'There is nothing to show here yet.';
  }
}
